import { createContext, useContext, useRef, useCallback, type ReactNode } from 'react';

interface LinkClickTracker {
  onDetailLoaded: (success: boolean, error?: string) => void;
  onNotFound: () => void;
}

interface AnalyticsContextValue {
  /** Register a link click and get the tracking callback */
  registerLinkClick: (linkId: number, tracker: LinkClickTracker) => void;
  /** Get the tracker for a link (called by LinkDetail) */
  getTracker: (linkId: number) => LinkClickTracker | undefined;
  /** Clear the tracker after use */
  clearTracker: (linkId: number) => void;
}

const AnalyticsContext = createContext<AnalyticsContextValue | null>(null);

export function AnalyticsProvider({ children }: { children: ReactNode }) {
  // Store trackers by linkId - use ref to avoid re-renders
  const trackersRef = useRef<Map<number, LinkClickTracker>>(new Map());

  const registerLinkClick = useCallback((linkId: number, tracker: LinkClickTracker) => {
    trackersRef.current.set(linkId, tracker);
    // Auto-cleanup after 30 seconds (if navigation never completes)
    setTimeout(() => {
      trackersRef.current.delete(linkId);
    }, 30000);
  }, []);

  const getTracker = useCallback((linkId: number) => {
    return trackersRef.current.get(linkId);
  }, []);

  const clearTracker = useCallback((linkId: number) => {
    trackersRef.current.delete(linkId);
  }, []);

  return (
    <AnalyticsContext.Provider value={{ registerLinkClick, getTracker, clearTracker }}>
      {children}
    </AnalyticsContext.Provider>
  );
}

export function useAnalytics() {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
}
